import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { brandService } from "@/shared/services/brandService";
import { HTTP_STATUS, brandConstants, authConstants } from "@/config/constants";
import { getUserThunk } from "@/features/profile/store/userSlice";

type BrandRecord = Record<string, any>;

type BrandState = {
  error: unknown;
  loading: boolean;
  pageSetup: BrandRecord | null;
  selectedBrand: BrandRecord | null;
  userBrands: BrandRecord[];
};

type RootState = {
  brand: BrandState;
};

export const getPageSetupThunk = createAsyncThunk(
  "brand/getPageSetup",
  async (brandId: string | number, { rejectWithValue }) => {
    try {
      const res = await brandService.getPageSetup(brandId);
      if (res.status === HTTP_STATUS.OK && res.data) return res.data.rows[0];
    } catch (err: any) {
      return rejectWithValue(err?.message || "Failed to fetch page setup");
    }
  }
);

export const getBrandsThunk = createAsyncThunk(
  "brand/getBrands",
  async (data: BrandRecord | undefined, { rejectWithValue }) => {
    try {
      const res = await brandService.getBrands(data);
      if (res.status === HTTP_STATUS.OK && res.data) {
        const brandData = res.data.rows;
        const brandMeta = {
          ...(res.data.is_terms_accepted !== undefined
            ? { is_terms_accepted: res.data.is_terms_accepted }
            : {}),
          ...(res.data.participant_accepted !== undefined
            ? { participant_accepted: res.data.participant_accepted }
            : {}),
        };

        if (brandData && brandData.length) {
          return brandData.map((brand: BrandRecord) => ({
            ...brand,
            ...brandMeta,
          }));
        }
      }
    } catch (err: any) {
      return rejectWithValue(err?.message || "Failed to fetch brands");
    }
  }
);

function updateSelectedBrandData(state: BrandState, brands: BrandRecord[]) {
  if (!state.selectedBrand) return null;
  const updated = brands.find(
    (b: BrandRecord) => b.id === state.selectedBrand?.id
  );
  return updated ? { ...state.selectedBrand, ...updated } : state.selectedBrand;
}

const initialState: BrandState = {
  userBrands: [],
  selectedBrand: null,
  pageSetup: null,
  error: null,
  loading: false,
};

const brandSlice = createSlice({
  name: "brand",
  initialState,
  reducers: {
    setGlobalBrand(state, action: PayloadAction<BrandRecord | null>) {
      state.selectedBrand = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPageSetupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPageSetupThunk.fulfilled, (state, action) => {
        state.pageSetup = action.payload;
        state.loading = false;
      })
      .addCase(getPageSetupThunk.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getBrandsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBrandsThunk.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.userBrands = action.payload;
        state.selectedBrand = updateSelectedBrandData(state, action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(getBrandsThunk.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getUserThunk.fulfilled, (state, action) => {
        const brands = action.payload?.brandData || [];
        if (!brands.length) return;
        state.userBrands = brands;
        state.selectedBrand = updateSelectedBrandData(state, brands);
        state.loading = false;
        state.error = null;
      })
      // Legacy action types dispatched by authSaga / userSlice
      .addCase(
        brandConstants.GET_BRANDS_SUCCESS,
        (state, action: PayloadAction<BrandRecord[] | undefined>) => {
          const brands = action.payload || [];
          state.userBrands = brands;
          state.selectedBrand = updateSelectedBrandData(state, brands);
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(
        brandConstants.SET_GLOBAL_BRAND,
        (state, action: PayloadAction<BrandRecord | null>) => {
          state.selectedBrand = action.payload;
        }
      )
      .addCase(
        authConstants.UPDATE_BRAND_DATA,
        (state, action: PayloadAction<BrandRecord[]>) => {
          state.userBrands = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(authConstants.LOGOUT, () => initialState);
  },
});

export const { setGlobalBrand } = brandSlice.actions;
export default brandSlice.reducer;

// Selectors
export const selectedBrandSelector = (state: RootState) =>
  state.brand.selectedBrand;
export const userBrandsSelector = (state: RootState) => state.brand.userBrands;
export const pageSetupSelector = (state: RootState) => state.brand.pageSetup;
export const brandIsLoadingSelector = (state: RootState) => state.brand.loading;
